import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { UsersFacade } from '@app/shared/+state/users/users.facade';
import { PaginationMeta } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { DemoService } from '@app/shared/services/demo/demo.service';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, interval, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { Code, Device, MatchedDevice, MatchedThermostat } from '../../data/smart-devices.interface';
import { SmartDevicesDialogService } from '../../dialogs/smart-devices-dialog.service';
import { SmartDevicesService } from '../../service/smart-devices.service';

const TAB_THERMOSTATS = 3;

@Component({
  standalone: false,
  templateUrl: `device-dashboard.component.html`,
  styleUrls: [`device-dashboard.component.scss`],
})
export class DeviceDashboardComponent implements OnDestroy {
  private dashboardRefreshIntervalMs = 60_000;

  private reservationsPageSubject = new BehaviorSubject<number>(1);
  private reservationsSearchSubject = new BehaviorSubject<string>('');
  private reservationsFiltersSubject = new BehaviorSubject<Filter[]>([]);

  private operationsPageSubject = new BehaviorSubject<number>(1);
  private operationsSearchSubject = new BehaviorSubject<string>('');
  private operationsFiltersSubject = new BehaviorSubject<Filter[]>([]);

  private devicesPageSubject = new BehaviorSubject<number>(1);
  private devicesSearchSubject = new BehaviorSubject<string>('');
  private devicesFiltersSubject = new BehaviorSubject<Filter[]>([]);

  private thermostatsPageSubject = new BehaviorSubject<number>(1);
  private thermostatsSearchSubject = new BehaviorSubject<string>('');
  private thermostatsFiltersSubject = new BehaviorSubject<Filter[]>([]);

  private refreshDataSubscription: Subscription;
  intervalSubscription: Subscription;

  private showLoadingOnNextRefresh = true;

  reservations$: Observable<{ data: Code[] | null; meta?: PaginationMeta; loading: boolean; error: string | null }>;
  operations$: Observable<{ data: Code[] | null; meta?: PaginationMeta; loading: boolean; error: string | null }>;
  devices$: Observable<{ data: MatchedDevice[]; meta?: PaginationMeta; loading: boolean; error: string | null }>;
  thermostats$: Observable<{
    data: MatchedThermostat[];
    meta?: PaginationMeta;
    loading: boolean;
    error: string | null;
  }>;

  canCreateManualCode$: Observable<boolean> = this.permissionService.hasAccessTo(
    Features.PERM_SMARTLOCKS_DASHBOARD_WRITE
  );

  selectedTabIndex = 0;
  destroy$ = new Subject<void>();

  constructor(
    private smartService: SmartDevicesService,
    private dialogService: SmartDevicesDialogService,
    private permissionService: UserPermissionsService,
    private demoService: DemoService,
    private activatedRoute: ActivatedRoute,
    private usersFacade: UsersFacade
  ) {
    // Refresh guest when the service signals that something has changed
    this.refreshDataSubscription = this.smartService.refreshReservationsAndDevicesFromApi$.subscribe(() => {
      this.showLoadingOnNextRefresh = false;
      this.reservationsPageSubject.next(this.reservationsPageSubject.getValue());
      this.operationsPageSubject.next(this.operationsPageSubject.getValue());
      this.devicesPageSubject.next(this.devicesPageSubject.getValue());
      this.thermostatsPageSubject.next(this.thermostatsPageSubject.getValue());
    });

    // Refresh interval for 60 seconds for all data (operations, reservations and devices)
    this.intervalSubscription = interval(this.dashboardRefreshIntervalMs).subscribe(() => {
      this.showLoadingOnNextRefresh = false;
      this.reservationsPageSubject.next(this.reservationsPageSubject.getValue());
      this.operationsPageSubject.next(this.operationsPageSubject.getValue());
      this.devicesPageSubject.next(this.devicesPageSubject.getValue());
      this.thermostatsPageSubject.next(this.thermostatsPageSubject.getValue());
    });

    this.reservations$ = combineLatest([
      this.reservationsPageSubject,
      this.reservationsSearchSubject,
      this.reservationsFiltersSubject,
    ]).pipe(
      switchMap(([page, search, filters]) =>
        this.smartService.getCodes((page - 1) * 20, search, filters, 'reservation').pipe(
          map((data) => ({
            data: data.data,
            meta: data.meta.pagination,
            loading: false,
            error: null,
          })),
          this.showLoadingOnNextRefresh ? startWith({ data: null, loading: true, error: null }) : tap(),
          catchError(() => of({ data: null, loading: false, error: 'Error fetching reservations' }))
        )
      )
    );

    this.operations$ = combineLatest([
      this.operationsPageSubject,
      this.operationsSearchSubject,
      this.operationsFiltersSubject,
    ]).pipe(
      switchMap(([page, search, filters]) =>
        this.smartService.getCodes((page - 1) * 20, search, filters, 'manual').pipe(
          map((data) => ({
            data: data.data,
            meta: data.meta.pagination,
            loading: false,
            error: null,
          })),
          this.showLoadingOnNextRefresh ? startWith({ data: null, loading: true, error: null }) : tap(),
          catchError(() => of({ data: null, loading: false, error: 'Error fetching operations' }))
        )
      )
    );

    this.devices$ = combineLatest([
      this.devicesPageSubject,
      this.devicesSearchSubject,
      this.devicesFiltersSubject,
    ]).pipe(
      switchMap(([page, search, filters]) =>
        this.smartService.getDevices((page - 1) * 20, search, filters).pipe(
          map((data) => ({
            data: data.data,
            meta: data.meta.pagination,
            loading: false,
            error: null,
          })),
          this.showLoadingOnNextRefresh ? startWith({ data: null, loading: true, error: null }) : tap(),
          catchError(() => of({ data: null, loading: false, error: 'Error fetching devices' }))
        )
      )
    );

    this.thermostats$ = combineLatest([
      this.thermostatsPageSubject,
      this.thermostatsSearchSubject,
      this.thermostatsFiltersSubject,
    ]).pipe(
      switchMap(([page, search, filters]) =>
        this.smartService.getThermostats((page - 1) * 20, search, filters).pipe(
          map((data) => ({
            data: data.data,
            meta: data.meta.pagination,
            loading: false,
            error: null,
          })),
          this.showLoadingOnNextRefresh ? startWith({ data: null, loading: true, error: null }) : tap(),
          catchError(() => of({ data: null, loading: false, error: 'Error fetching devices' }))
        )
      )
    );
  }

  openGuestDetailsModal(reservationCode: string) {
    this.dialogService.openCodeDialog(reservationCode);
  }

  openDeviceDetailsModal({ deviceId, deviceType }: { deviceId: Device['id']; deviceType: Device['device_type'] }) {
    this.dialogService.openDeviceDialog(deviceId, deviceType);
  }

  openThermostatDetailsModal({ deviceId, deviceType }: { deviceId: Device['id']; deviceType: Device['device_type'] }) {
    this.dialogService.openThermostatDialog(deviceId, deviceType);
  }

  openAddManualCodeModal() {
    this.dialogService.openCodeCreateEditDialog();
  }

  onReservationsPageChanged(newPageNo: number) {
    this.showLoadingOnNextRefresh = true;
    this.reservationsPageSubject.next(newPageNo);
  }

  onDevicesPageChanged(newPageNo: number) {
    this.showLoadingOnNextRefresh = true;
    this.devicesPageSubject.next(newPageNo);
  }

  onThermostatsPageChanged(newPageNo: number) {
    this.showLoadingOnNextRefresh = true;
    this.thermostatsPageSubject.next(newPageNo);
  }

  onOperationsPageChanged(newPageNo: number) {
    this.showLoadingOnNextRefresh = true;
    this.operationsPageSubject.next(newPageNo);
  }

  onReservationsSearchChanged(searchTerm: string) {
    this.showLoadingOnNextRefresh = true;
    this.reservationsSearchSubject.next(searchTerm);
    // Reset the reservations page to 1 when search query changes
    this.reservationsPageSubject.next(1);
  }

  onDevicesSearchChanged(searchTerm: string) {
    this.showLoadingOnNextRefresh = true;
    this.devicesSearchSubject.next(searchTerm);
    // Reset the devices page to 1 when search query changes
    this.devicesPageSubject.next(1);
  }

  onThermostatsSearchChanged(searchTerm: string) {
    this.showLoadingOnNextRefresh = true;
    this.thermostatsSearchSubject.next(searchTerm);
    // Reset the thermostats page to 1 when search query changes
    this.thermostatsPageSubject.next(1);
  }

  onOperationsSearchChanged(searchTerm: string) {
    this.showLoadingOnNextRefresh = true;
    this.operationsSearchSubject.next(searchTerm);
    // Reset the operations page to 1 when search query changes
    this.operationsPageSubject.next(1);
  }

  onReservationsFiltersChanged(filters: Filter[]) {
    this.showLoadingOnNextRefresh = true;
    this.reservationsFiltersSubject.next(filters);
    // Reset the reservations page to 1 when filters change
    this.reservationsPageSubject.next(1);
  }

  onDevicesFiltersChanged(filters: Filter[]) {
    this.showLoadingOnNextRefresh = true;
    this.devicesFiltersSubject.next(filters);
    // Reset the guests page to 1 when filters change
    this.devicesPageSubject.next(1);
  }

  onThermostatsFiltersChanged(filters: Filter[]) {
    this.showLoadingOnNextRefresh = true;
    this.thermostatsFiltersSubject.next(filters);
    // Reset the thermostats page to 1 when filters change
    this.thermostatsPageSubject.next(1);
  }

  onOperationsFiltersChanged(filters: Filter[]) {
    this.showLoadingOnNextRefresh = true;
    this.operationsFiltersSubject.next(filters);
    // Reset the operations page to 1 when filters change
    this.operationsPageSubject.next(1);
  }

  ngOnDestroy() {
    this.refreshDataSubscription.unsubscribe();
    this.intervalSubscription.unsubscribe();
    this.destroy$.next();
  }

  get smartlockFilterUrl() {
    if (this.demoService.isDemo()) {
      return `${environment.apiUrl}/filters/`;
    } else {
      return `${environment.smartDevicesApiUrl}/filters/`;
    }
  }
}
